import router from './router'
import { ElMessage } from 'element-plus'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import { getToken } from '@/utils/auth'
import { isHttp } from '@/utils/validate'
import { isRelogin } from '@/utils/request'
import appStore from '@/store/modules/app';
import useDictStore from '@/store/modules/dict';
import appConfig from '@/store/modules/config';
import useUserStore from '@/store/modules/user'
import useSettingsStore from '@/store/modules/settings'
import usePermissionStore from '@/store/modules/permission';
import screenRouters from '@/router/modules/screen';
import { COMMON_SYSTEM_CODE_LIST } from '@/api/constant/constant.js';
import { forceReload, fetchLatestVersion, resetForceReloadTimes, VERSION_PARAM } from '@/utils/forceReload'

NProgress.configure({ showSpinner: false });

const pathList = screenRouters.map(item => item.path);

const whiteList = ['/login', '/auth-redirect', '/bind', '/register', ...pathList];

// 版本号在 localStorage 中的存储 key
const VERSION_KEY = 'kl-web-version';

// 版本检查的节流间隔(毫秒),避免每次路由跳转都请求一次 version.json
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000;
// 是否已经执行过第一次版本检查
let versionChecked = false;
let lastCheckTime = 0;

/**
 * 检查前端版本号,发现不一致时强制刷新页面
 * @param {boolean} [force] 为 true 时跳过节流限制(应用启动首次检查时使用)
 */
const checkWebVersion = async (force = false) => {
  // 修复:原实现在每次路由跳转时都请求一次 version.json,这里加上节流;
  // 首次检查(force=true)不受节流限制
  if (!force && Date.now() - lastCheckTime < VERSION_CHECK_INTERVAL) return
  lastCheckTime = Date.now()
  try {
    const version = await fetchLatestVersion();
    if (!version) return
    const localVersion = localStorage.getItem(VERSION_KEY);
    // 地址栏上携带的版本号:说明当前页面是通过强制刷新跳转过来的
    const urlVersion = new URL(window.location.href).searchParams.get(VERSION_PARAM);
    if (urlVersion === version) {
      // 修复:已经跳到最新版本了,此时才写入 localStorage
      // 原实现是"先写入新版本号再刷新",一旦刷新后仍拿到旧代码,版本号已被覆盖,
      // 之后就不会再触发刷新,用户将永久停留在旧版本上
      localStorage.setItem(VERSION_KEY, version);
      resetForceReloadTimes();
      return
    }
    if (localVersion == null || version === localVersion) {
      // 首次访问,或者当前运行的已经是最新版本
      localStorage.setItem(VERSION_KEY, version);
      return
    }
    // 版本不一致,强制刷新获取最新代码(此处不写 localStorage,交给刷新后的新页面写入)
    await forceReload('检测到系统更新,即将刷新页面!');
  }catch(e){
    console.error("检查版本号出错", e)
  }
}

router.beforeEach((to, from, next) => {
  useSettingsStore().setTitle(to?.meta?.title)
  // 检查版本号
  // 修复:原判断 from.path !== "/" 会导致用户直接打开首页时整个版本检查被跳过,
  // 现改为首次导航强制检查一次,之后按节流间隔复查
  checkWebVersion(!versionChecked);
  versionChecked = true;
  const systemCode = to.meta?.systemCode;
  if(systemCode != null && COMMON_SYSTEM_CODE_LIST.indexOf(systemCode) < 0){
    // 找到对应的子系统信息
    const systemList = appStore().systemList;
    const findSystem = systemList.find(item => item.systemCode === systemCode);
    // console.log("找到的子系统信息", findSystem);
    if(findSystem){
      if(findSystem.windowOpen == "Y"){
        window.open(`${findSystem.rootPath}${to.path}`, "_blank");
      }else{
        window.location.href = `${findSystem.rootPath}${to.path}`;
      }
      return;
    }
  }

  if(to.meta.layoutType){
    console.log("to", to);
  }

  NProgress.start()
  if (getToken()) {
    /* has token*/
    if (to.path === '/login') {
      next({ path: '/' })
      NProgress.done()
    } else {
      if (useUserStore().roles.length === 0) {
        isRelogin.show = true
        // 判断当前用户是否已拉取完user_info信息
        useUserStore().getInfo().then(async () => {
          isRelogin.show = false
          // 初始化得到数据字典信息
          useDictStore().init();
          appConfig().init();
          appStore().setSystemList();
          appStore().i18nLanguageConfigInit();
          await usePermissionStore().querylistSettingRouter();
          usePermissionStore().generateRoutes().then(accessRoutes => {
            // 根据roles权限生成可访问的路由表
            accessRoutes.forEach(route => {
              // console.log("route.path", route.path)
              if (!isHttp(route.path)) {
                router.addRoute(route) // 动态添加可访问路由表
              }
            })
            next({ ...to, replace: true }) // hack方法 确保addRoutes已完成
          })
        }).catch(err => {
          useUserStore().logOut().then(() => {
            ElMessage.error(err)
            next({ path: '/' })
          })
        })
      } else {
        if(to.path == "/work/index"){
          // 判断, 查找是否存在此类路由
          const filterList = usePermissionStore().routerList.filter(item => item.path === "index");
          if(!filterList || filterList.length == 0){
            // 需要设置动态路由, 获取第一级最底层路由
            const topbarRouters = usePermissionStore().topbarRouters;
            const findRouter = finxdSubRouter(topbarRouters[0]);
            next(`${findRouter.path.indexOf("/") === 0 ? '' : '/'}${findRouter.path}`);
            return;
          }
        }
        next()
      }
    }
  } else {
    // 没有token
    if (whiteList.indexOf(to.path) !== -1) {
      // 在免登录白名单，直接进
      next()
    } else {
      next(`/login?redirect=${to.fullPath}`) // 否则全部重定向到登录页
      NProgress.done()
    }
  }
})

router.afterEach(() => {
  NProgress.done()
})

// 动态路由组件加载失败时的自愈兜底
// 场景:发版后旧页面缓存了旧的 index.html,点击菜单加载动态组件时请求的 js 已被服务器删除(404),
// vue-router 会走到这里。捕获后强制刷新获取最新代码,避免"点击菜单没反应"
router.onError((error) => {
  const msg = error?.message || '';
  if (/Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(msg)) {
    forceReload('页面已更新,正在自动刷新...');
  }
})


const finxdSubRouter = (targetRoute) => {
  // 需要深拷贝数据,并且将数据中的隐藏的菜单剔除掉
  let targetRouteCopy = JSON.parse(JSON.stringify(targetRoute));
  let delHidden = (targetRouteCopy) => {
    if(targetRouteCopy && targetRouteCopy.children){
      for(let i=0;i<targetRouteCopy.children.length;i++){
        if(targetRouteCopy.children[i].hidden){
          targetRouteCopy.children.splice(i, 1);
          i--;
        }else{
          delHidden(targetRouteCopy.children[i])
        }
      }
    }
  }
  delHidden(targetRouteCopy);
  let { children, path, name } = targetRouteCopy;
  while (children && children[0]) {
    path = `${path && path != '' && path != '/' ? path + '/' : ''}` + children[0].path;
    name = children[0].name;
    children = children[0].children;
  }

  return { children, path, name };
}